import { BitmapText, Container, Point as PixiPoint, Sprite, type Texture } from "pixi.js";
import type { Log, PresentationTaskRunner } from "@slot/presentation";
import type { PotKind } from "../rules/symbols";
import { FIRE_STREAK_TUNING } from "../tuning/featureTuning";
import { coinText } from "../views/textStyles";
import { BoardImpact } from "./BoardImpact";
import { CellHits } from "./CellHits";
import { arc, type Point } from "./flightPath";
import { FireStreaks } from "./FireStreaks";
import { FlyingLights } from "./FlyingLights";
import { MysteryReel } from "./MysteryReel";
import { FX_SCOPE } from "./fxScope";
import { ERUPTION_BURST, IMPACT_BURST, PotBursts } from "./PotBursts";
import { POT_COLOURS } from "./PotsView";

/**
 * The flights and bursts over the composition, in its space: a gem's flying light from its coin to its pot and its
 * impact there, a pot's eruption and its fire streaks falling onto the board (LIFE's onto the meter, MULTI's onto its
 * cells), a value from a coin to a collect or the bonus's total, sparkles where something lands or twinkling on the
 * gold. Each piece lives for its motion and is destroyed after; motion runs on the runner it is given (the
 * presentation's), a twinkle on the timeline it names.
 */
export class EffectsLayer extends Container {
  /**
   * The artist's art (it comes in after the game starts): the flying lights, the impacts, the eruptions, the streaks,
   * GROW's board impact.
   */
  public readonly lights: FlyingLights;
  public readonly impacts: PotBursts;
  public readonly eruptions: PotBursts;
  public readonly streaks: FireStreaks;
  public readonly boardImpact: BoardImpact;
  /** MULTI's bursts as its fireballs reach their cells. */
  public readonly cellHits: CellHits;
  /** A mystery jackpot's reel as it reveals its jackpot, over the board. */
  public readonly mysteryReel: MysteryReel;
  private readonly runner: PresentationTaskRunner;
  private readonly glow: Texture | null;
  private readonly sparkle: Texture | null;
  /** The screen's size, in the stage's units (its CSS pixels): where the streaks that pass it fall past. */
  private readonly screen: (() => { width: number; height: number }) | null;

  public constructor(input: {
    glow: Texture | null;
    sparkle: Texture | null;
    runner: PresentationTaskRunner;
    ambientRunner: PresentationTaskRunner;
    log: Log;
    /** A multiplier's "×n" to ride a MULTI streak's head; without it, none rides. */
    streakNumber?: (text: string) => Container;
    /** The screen's size; without it, no streak passes the screen. */
    screen?: () => { width: number; height: number };
  }) {
    super({ label: "FxOverlays" });
    this.runner = input.runner;
    this.glow = input.glow;
    this.sparkle = input.sparkle;
    this.screen = input.screen ?? null;
    this.lights = new FlyingLights({ runner: input.runner, ambientRunner: input.ambientRunner, log: input.log });
    this.impacts = new PotBursts({ burst: IMPACT_BURST, ambientRunner: input.ambientRunner, log: input.log });
    this.eruptions = new PotBursts({ burst: ERUPTION_BURST, ambientRunner: input.ambientRunner, log: input.log });
    this.streaks = new FireStreaks({ runner: input.runner, ambientRunner: input.ambientRunner, log: input.log, ...(input.streakNumber ? { numberLabel: input.streakNumber } : {}) });
    this.boardImpact = new BoardImpact({ ambientRunner: input.ambientRunner, log: input.log });
    this.cellHits = new CellHits({ ambientRunner: input.ambientRunner, log: input.log });
    this.mysteryReel = new MysteryReel({ runner: input.runner });
    // GROW's impact bursts over the board, its coins and the frame's fire; an eruption's jets leave over the plaques; a
    // light dives in over its impact's flash; a cell's hit bursts under the streaks, whose numbers pop through it; the
    // streaks fall over all but a mystery jackpot's reel, grown over the board at the bonus's end.
    this.addChild(this.boardImpact, this.eruptions, this.impacts, this.lights, this.cellHits, this.streaks, this.mysteryReel);
  }

  /** A light reaching `pot`'s opening (`at`): the artist's impact there; until the impacts are in, a burst of `tint`. */
  public impact(pot: PotKind, at: Point, tint: number): void {
    if (this.impacts.ready) this.impacts.play(pot);
    else this.burst({ at, tint, count: 6, radius: 90, seconds: 0.4 });
  }

  /** `pot` erupts: the artist's eruption on its opening (nothing until the eruptions are in). */
  public erupt(pot: PotKind): void {
    this.eruptions.play(pot);
  }

  /**
   * A pot's fire streak onto `to`: the artist's fireball in its colour, falling straight down onto it from over the
   * screen's top, a `label` (a multiplier's "×n") riding its head, caught on `to` and settling on `label.to`; until the
   * streaks are in, `fallback` flies from the pot to its own point as a sprite instead, for its `seconds`. It resolves as
   * it lands (with a label, once the label has settled): true when the artist's streak fell.
   */
  public async fallStreak(input: {
    pot: PotKind;
    to: Point;
    label?: { text: string; to: Point; size: number };
    /** As it lands: an impact of the caller's; `ring` false, the streak plays none of its own. */
    onTouch?: () => void;
    ring?: boolean;
    fallback: { from: Point; to: Point; lift: number; seconds: number; texture: Texture | null; size: number; tint: number; scale?: { from: number; peak: number; to: number } };
  }): Promise<boolean> {
    // The screen's top edge in this layer's space: the canvas's corner, brought in.
    if (this.streaks.ready) {
      const { label, onTouch, ring } = input;
      return this.streaks.fall({ pot: input.pot, to: input.to, top: this.toLocal(new PixiPoint(0, 0)).y, ...(label ? { label } : {}), ...(onTouch ? { onTouch } : {}), ...(ring === undefined ? {} : { ring }) });
    }
    await this.fly(input.fallback);
    return false;
  }

  /**
   * `pot`'s streaks that land nowhere, one down each line in `at` (this layer's x: a column's centre), each falling
   * straight through the screen past its bottom: the first the tuning's while after it is asked for (behind the landing
   * ones that set out with it), the rest at its gap. Nothing before the streaks are in or without the screen's size. It
   * resolves once all are past.
   */
  public async passStreaks(input: { pot: PotKind; at: readonly number[] }): Promise<void> {
    const screen = this.screen?.();
    if (!this.streaks.ready || !screen) return;
    const top = this.toLocal(new PixiPoint(0, 0)).y;
    const bottom = this.toLocal(new PixiPoint(0, screen.height)).y;
    const passes: Promise<void>[] = [];
    const { afterSeconds, gapSeconds } = FIRE_STREAK_TUNING.passers;
    for (const [i, x] of input.at.entries()) {
      await this.runner.sleep(i === 0 ? afterSeconds : gapSeconds, FX_SCOPE);
      passes.push(this.streaks.pass({ pot: input.pot, x, top, bottom }));
    }
    await Promise.all(passes);
  }

  /**
   * A pot's flying light from `from` to `to` on an arc: the artist's comet in its colour, at its own pace; until the
   * lights are in, `fallback` flies as a sprite instead, for `seconds`.
   */
  public async flyLight(input: {
    pot: PotKind;
    from: Point;
    to: Point;
    lift: number;
    seconds: number;
    fallback: { texture: Texture | null; size: number; tint: number; scale?: { from: number; peak: number; to: number } };
    /** Runs once, its seconds before the light reaches `to` (the sprite's: as it lands). */
    beforeArrival?: { seconds: number; run: () => void };
  }): Promise<void> {
    const { pot, from, to, lift, seconds, fallback, beforeArrival } = input;
    if (this.lights.ready) return this.lights.fly({ pot, from, to, lift, beforeArrival });
    await this.fly({ ...fallback, from, to, lift, seconds });
    beforeArrival?.run();
  }

  /** A sprite (a gem, a badge) flying on an arc, growing to `scale.peak` midway and landing at `scale.to`. */
  public async fly(input: { texture: Texture | null; size: number; tint?: number; from: Point; to: Point; lift: number; seconds: number; scale?: { from: number; peak: number; to: number } }): Promise<void> {
    if (!input.texture) return;
    const piece = new Container({ label: "Flight" });
    if (this.glow) {
      const halo = new Sprite({ texture: this.glow, anchor: 0.5, tint: input.tint ?? 0xffffff, alpha: 0.7 });
      halo.setSize(input.size * 1.8, input.size * 1.8);
      piece.addChild(halo);
    }
    const art = new Sprite({ texture: input.texture, anchor: 0.5 });
    art.setSize(input.size, input.size);
    piece.addChild(art);
    this.addChild(piece);
    const scale = input.scale ?? { from: 1, peak: 1.3, to: 0.8 };
    await this.runner.tweenProgress(input.seconds, (k) => {
      if (piece.destroyed) return;
      const p = arc(input.from, input.to, input.lift, k);
      piece.position.set(p.x, p.y);
      const s = k < 0.5 ? scale.from + (scale.peak - scale.from) * (k / 0.5) : scale.peak + (scale.to - scale.peak) * ((k - 0.5) / 0.5);
      piece.scale.set(s, s);
    }, FX_SCOPE, { easing: "quadInOut" });
    piece.destroy({ children: true });
  }

  /**
   * A cell's amount carried to the bonus's total: the artist's comet in MULTI's red, a gem's light's size, from `from` to
   * `to`, bowed `lift`, at `speed`, `text` riding its head in the coins' digits; until the lights are in, the amount
   * flies on its own for `seconds`. It sets out in a burst of its red, as a gem's light leaves its coin, and resolves as
   * it arrives.
   */
  public async flyToTotal(input: { text: string; from: Point; to: Point; lift: number; speed: number; seconds: number; fontSize: number }): Promise<void> {
    const { text, from, to, lift, speed, seconds, fontSize } = input;
    this.burst({ at: from, tint: POT_COLOURS.multi.glow, count: 6, radius: 90, seconds: 0.4 });
    if (!this.lights.ready) return this.flyAmount({ text, from, to, lift, seconds, fontSize });
    const drawn = coinText(text, fontSize);
    const rider = new BitmapText({ text: drawn.text, style: { fontFamily: drawn.fontFamily, fontSize: drawn.fontSize }, label: "FlyingAmount" });
    rider.pivot.y = drawn.pivotY;
    rider.anchor.set(0.5);
    try {
      await this.lights.fly({ pot: "multi", from, to, lift, rider, speed });
    } finally {
      rider.destroy();
    }
  }

  /** A value (a coin's amount) flying from `from` to `to`, shrinking as it goes. */
  public async flyAmount(input: { text: string; from: Point; to: Point; lift: number; seconds: number; fontSize: number }): Promise<void> {
    // In the coin's own digits.
    const drawn = coinText(input.text, input.fontSize);
    const piece = new BitmapText({ text: drawn.text, style: { fontFamily: drawn.fontFamily, fontSize: drawn.fontSize }, label: "FlyingAmount" });
    piece.pivot.y = drawn.pivotY;
    piece.anchor.set(0.5);
    this.addChild(piece);
    await this.runner.tweenProgress(input.seconds, (k) => {
      if (piece.destroyed) return;
      const p = arc(input.from, input.to, input.lift, k);
      piece.position.set(p.x, p.y);
      const s = 1.15 - 0.45 * k;
      piece.scale.set(s, s);
    }, FX_SCOPE, { easing: "quadIn" });
    piece.destroy();
  }

  /**
   * A sparkle twinkling at `at` (fire and forget): it grows, turns and shrinks back to nothing, its light added to what
   * is under it. On the round's runner in its scope (a slam snaps it), or on `timeline` (the ambient one's).
   */
  public twinkle(input: { at: Point; size: number; tint: number; seconds: number; timeline?: { runner: PresentationTaskRunner; scope?: string } }): void {
    if (!this.sparkle) return;
    const node = new Sprite({ texture: this.sparkle, anchor: 0.5, tint: input.tint, label: "Twinkle" });
    node.blendMode = "add";
    node.setSize(input.size, input.size);
    const size = node.scale.x;
    node.scale.set(0, 0);
    node.position.set(input.at.x, input.at.y);
    this.addChild(node);
    const runner = input.timeline?.runner ?? this.runner;
    const scope = input.timeline ? input.timeline.scope : FX_SCOPE;
    void runner
      .tweenProgress(input.seconds, (k) => {
        if (node.destroyed) return;
        const s = size * Math.sin(Math.PI * k);
        node.scale.set(s, s);
        node.rotation = 1.2 * k;
      }, scope, { easing: "linear" })
      .then(() => node.destroy());
  }

  /** Sparkles bursting out of `at` in `tint`, fading as they fly (fire and forget). */
  public burst(input: { at: Point; tint: number; count: number; radius: number; seconds: number }): void {
    if (!this.sparkle) return;
    const pieces: { node: Sprite; angle: number; reach: number }[] = [];
    for (let i = 0; i < input.count; i++) {
      const node = new Sprite({ texture: this.sparkle, anchor: 0.5, tint: input.tint });
      node.setSize(56, 56);
      node.position.set(input.at.x, input.at.y);
      this.addChild(node);
      pieces.push({ node, angle: (i / input.count) * Math.PI * 2 + (i % 2) * 0.3, reach: input.radius * (0.7 + 0.3 * ((i * 7) % 5) / 4) });
    }
    void this.runner
      .tweenProgress(input.seconds, (k) => {
        for (const { node, angle, reach } of pieces) {
          if (node.destroyed) continue;
          node.position.set(input.at.x + Math.cos(angle) * reach * k, input.at.y + Math.sin(angle) * reach * k);
          node.alpha = 1 - k;
          node.rotation = k * 2;
        }
      }, FX_SCOPE, { easing: "quadOut" })
      .then(() => {
        for (const { node } of pieces) node.destroy();
      });
  }
}
